import type { CompRef } from '@wix/document-services-types';
import { ManifestComponentExtensionType } from '../stores';
import { AbstractExternalAPIHandler } from './AbstractExternalAPIHandler';
import { cloneDeep } from 'lodash';
import { ChangeType } from '../ManifestChangeDetection';
import { DependencyToken } from '../dependencies';
import type { EditorPointer } from '@wix/editor-document-logic-api';

export class ExternalDataHandler extends AbstractExternalAPIHandler {
  getData(
    editorPointer: EditorPointer<CompRef>,
    type: ManifestComponentExtensionType,
    options?: { customGetDefaultData: () => Record<string, unknown> },
  ): Record<string, unknown> {
    switch (type) {
      case ManifestComponentExtensionType.BUILDER:
      case ManifestComponentExtensionType.SYSTEM: {
        const getData = this.dependenciesContributor.get(
          DependencyToken.BUILDER_DATA_GET,
        );

        const savedData = getData(editorPointer) ?? {};

        return fillWithDefaults(
          /**
           * We needed cloneDeep because we return this object to the caller.
           * If the caller mutates a nested value of the response, it would affect the value we got from hostAPI.
           * This is something that happened.
           */
          cloneDeep(savedData),
          options?.customGetDefaultData() ?? {},
        );
      }

      case ManifestComponentExtensionType.OOI: {
        const manifest =
          this.manifestStore.getResolvedEditorElement(editorPointer);
        const metadata =
          this.manifestStore.getMetadataByEditorPointer<ManifestComponentExtensionType.OOI>(
            editorPointer,
          );

        const manifestData = manifest?.data;

        if (!manifestData) {
          return {};
        }

        const savedData: Record<string, string> = {};

        const getTPAData = this.dependenciesContributor.get(
          DependencyToken.OOI_DATA_GET,
        );

        Object.keys(manifestData).forEach((key) => {
          const dmKey = metadata?.statics.settingsParams?.[key]?.key ?? key;

          getTPAData(editorPointer, dmKey, (result) => {
            savedData[key] = result[dmKey];
          });
        });

        return fillWithDefaults(
          /**
           * We needed cloneDeep because we return this object to the caller.
           * If the caller mutates a nested value of the response, it would affect the value we got from hostAPI.
           * This is something that happened.
           */
          cloneDeep(savedData),
          options?.customGetDefaultData() ?? {},
        );
      }

      default: {
        const getData = this.dependenciesContributor.get(
          DependencyToken.UNKNOWN_COMPONENT_DATA_GET,
        );

        return fillWithDefaults(
          /**
           * We needed cloneDeep because we return this object to the caller.
           * If the caller mutates a nested value of the response, it would affect the value we got from hostAPI.
           * This is something that happened.
           */
          cloneDeep(getData(editorPointer) ?? {}),
          options?.customGetDefaultData() ?? {},
        );
      }
    }
  }

  setData(
    editorPointer: EditorPointer<CompRef>,
    type: ManifestComponentExtensionType,
    data: Record<string, unknown>,
  ) {
    switch (type) {
      case ManifestComponentExtensionType.BUILDER:
      case ManifestComponentExtensionType.SYSTEM: {
        const setData = this.dependenciesContributor.get(
          DependencyToken.BUILDER_DATA_UPDATE,
        );

        setData(editorPointer, data);
        break;
      }

      case ManifestComponentExtensionType.OOI: {
        const metadata =
          this.manifestStore.getMetadataByEditorPointer<ManifestComponentExtensionType.OOI>(
            editorPointer,
          );

        const dmData = Object.entries(data).reduce((acc, [key, value]) => {
          const dmKey = metadata?.statics.settingsParams?.[key]?.key ?? key;

          return {
            ...acc,
            [dmKey]: value,
          };
        }, {});

        const setData = this.dependenciesContributor.get(
          DependencyToken.OOI_DATA_UPDATE,
        );

        setData(editorPointer, dmData);

        break;
      }

      default: {
        const setData = this.dependenciesContributor.get(
          DependencyToken.UNKNOWN_COMPONENT_DATA_UPDATE,
        );

        setData(editorPointer, data);
      }
    }

    this.manifestChangeDetection.notify({
      type: ChangeType.Data,
      editorPointer,
      extensionType: type,
      value: data,
    });
  }
}

/**
 * default values assignment should be done on
 * the editor side according to the agreement
 */
function fillWithDefaults(
  data: Record<string, unknown>,
  defaults: Record<string, unknown>,
) {
  const result = { ...defaults };

  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined && value !== null) {
      result[key] = value;
    }
  }

  return result;
}
